// next-app/src/hooks/use-check-in.ts
'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useCheckStore } from '@/store/check-store';
import { useAuthStore } from '@/store/auth-store';
import { queryKeys } from './query-keys';
import type { CheckInDataResponse } from '@/types';

// ============================================================================
// Check-in Data Query
// ============================================================================

interface UseCheckInDataOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

/**
 * Hook to fetch check-in data for an event.
 *
 * Usage:
 * ```tsx
 * const { data, isLoading } = useCheckInData(eventId);
 * ```
 */
export function useCheckInData(eventId: number, options: UseCheckInDataOptions = {}) {
  const token = useAuthStore((state) => state.dealerToken);
  const setCheckInData = useCheckStore((state) => state.setCheckInData);

  return useQuery({
    queryKey: queryKeys.check.inData(eventId),
    queryFn: async () => {
      const { response, result } = await api.getCheckInData(eventId);

      if (!response.ok) {
        throw new Error('Failed to load check-in data');
      }

      if (result) {
        setCheckInData(result.participants, result.vehicles);
      }

      return result as CheckInDataResponse;
    },
    enabled: options.enabled !== false && Boolean(token) && Boolean(eventId),
    refetchInterval: options.refetchInterval ?? 30000, // Default: refresh every 30 seconds
    staleTime: 10000, // 10 seconds
  });
}

// ============================================================================
// Check-in Mutation
// ============================================================================

interface UseCheckInOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to check in a participant.
 *
 * Usage:
 * ```tsx
 * const checkIn = useCheckIn(eventId);
 * checkIn.mutate(participantId);
 * ```
 */
export function useCheckIn(eventId: number, options: UseCheckInOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (participantId: number) => {
      const { response, result } = await api.checkIn({
        participant_id: participantId,
        event_id: eventId,
      });

      if (!response.ok) {
        throw new Error('Failed to check in participant');
      }

      return { participantId, result };
    },
    onSuccess: () => {
      // Invalidate check-in data to refresh the list
      queryClient.invalidateQueries({ queryKey: queryKeys.check.inData(eventId) });

      // Also invalidate stats if being viewed
      queryClient.invalidateQueries({ queryKey: queryKeys.stats.event(eventId) });

      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useCheckIn] Error:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Selectors for filtered participants
// ============================================================================

/**
 * Hook to get filtered check-in participants based on search term.
 */
export function useFilteredCheckInParticipants() {
  const participants = useCheckStore((state) => state.checkInParticipants);
  const search = useCheckStore((state) => state.checkInSearch);

  if (!search) {
    // Return only participants who haven't checked in
    return participants.filter((p) => !p.checked_in);
  }

  const searchLower = search.toLowerCase();

  return participants.filter(
    (p) =>
      !p.checked_in &&
      (p.first_name.toLowerCase().includes(searchLower) ||
        p.last_name.toLowerCase().includes(searchLower) ||
        p.email.toLowerCase().includes(searchLower) ||
        p.phone.includes(search))
  );
}
